/** Based on web/src/utils/displayHelpers.ts */

import { tz as momentTz, tz, MomentInput } from 'moment-timezone';
import moment from 'moment';

import { MedelyDateTime } from '@medely/date-time-tools';
import { DisputeStatus, IDispute, IJob, IPayout, JobStatus } from '@medely/types';
import { Job, JobTypeList, Location } from 'graphql/generated/graphql';
import { InfoBadgeColor } from '@medely/ui-kit';
import { TAXABLE_TYPE_LABEL_OVERRIDE } from '../constants';
import { JobForIsOvernight, isOvernight } from './jobUtils';
import { pluralize } from './text';
import { centsToCurrency } from './centsToCurrency';

type JobTimeOptions = {
  precision?: 0 | 1 | 2;
  withShiftLength?: boolean;
  scheduledShiftStyle?: boolean;
};

type JobTimeInput = {
  current_starts_time?: string | null;
  current_ends_time?: string | null;
  approved_clock_in?: string | null;
  approved_clock_out?: string | null;
  guaranteed_minimum_hours?: number | null;
  status?: string | null;
  shift_type?: string | null;
  assignment?: {
    primary_location?: {
      timezone_lookup?: string | null;
    } | null;
    [key: string]: unknown;
  } | null;
  location?: {
    timezone_lookup?: string | null;
  } | null;
  options?: JobTimeOptions;
};

type TimecardJobForTime = Pick<IJob, 'current_starts_time' | 'current_ends_time' | 'shift_type'> & {
  assignment?: {
    primary_location?: {
      timezone_lookup?: string | null;
    } | null;
  } | null;
  location?: {
    timezone_lookup?: string | null;
  } | null;
  options?: JobTimeOptions;
};

const humanizeDate = (date: string, tz = 'system') =>
  new MedelyDateTime(date, { tz }).humanizedDate;

const getDate = (date: string) => new MedelyDateTime(date, { tz: 'system' }).format('MM/dd/yy');

const getYear = (date: string) => new MedelyDateTime(date, { tz: 'system' }).format('yyyy');

const titleize = (input?: string | null) =>
  input?.replace(/_/g, ' ').replace(/(?:^|\s|-)\S/g, (x) => x.toUpperCase());
const taxableTypeAsTitle = (taxable_type: string) => {
  return (
    TAXABLE_TYPE_LABEL_OVERRIDE[taxable_type as keyof typeof TAXABLE_TYPE_LABEL_OVERRIDE] ||
    titleize(taxable_type)
  );
};

function jobTotalHoursNumber({
  current_starts_time,
  current_ends_time,
}: IJob | Partial<IJob>): number {
  const start = moment(current_starts_time);
  const end = moment(current_ends_time);
  const hours = end.diff(start, 'hours', true);
  const allDayThreshold = 23.98;

  if (Math.round(hours) >= allDayThreshold) {
    return 24;
  }

  return hours;
}

const jobTimeInternal = ({
  currentEndsTime,
  currentStartsTime,
  approvedClockIn,
  approvedClockOut,
  jobTimezone,
  guaranteedMinimumHours,
  status,
  options = {},
}: {
  currentEndsTime?: string | null;
  currentStartsTime?: string | null;
  approvedClockIn?: string | null;
  approvedClockOut?: string | null;
  jobTimezone?: string | null;
  guaranteedMinimumHours?: number | null;
  status?: string | null;
  options?: JobTimeOptions;
}): string | null => {
  if (!currentStartsTime || !currentEndsTime) {
    return null;
  }

  const timezone = jobTimezone || momentTz.guess();
  const startsTime = approvedClockIn || currentStartsTime;
  const endsTime = approvedClockOut || currentEndsTime;
  const start = new MedelyDateTime(startsTime, { tz: timezone });
  const end = new MedelyDateTime(endsTime, { tz: timezone });

  const hoursDiff = start.difference(end, 'hours', options.precision).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const hours =
    status === 'canceled_with_fee'
      ? guaranteedMinimumHours
      : Math.max(guaranteedMinimumHours ?? 0, +hoursDiff);

  const showHours = options.withShiftLength !== false;

  return options.scheduledShiftStyle
    ? `${start.militaryTime} - ${end.militaryTime}${
        showHours ? ` (${pluralize(hours ?? 0, 'hr')})` : ``
      }`
    : `${start.militaryTime}-${end.militaryTime}${
        showHours ? ` • ${pluralize(hours ?? 0, 'hr')}` : ``
      }`;
};

function jobTime(
  job: JobTimeInput | (Partial<IJob> & { options?: JobTimeOptions }),
): string | null {
  const isTimeOff = (job.shift_type as string) === 'time_off';

  const jobTimezone = isTimeOff
    ? job.assignment?.primary_location?.timezone_lookup ?? undefined
    : job.location?.timezone_lookup ?? undefined;

  return jobTimeInternal({
    currentStartsTime: job.current_starts_time ?? undefined,
    currentEndsTime: job.current_ends_time ?? undefined,
    approvedClockIn: job.approved_clock_in ?? undefined,
    approvedClockOut: job.approved_clock_out ?? undefined,
    guaranteedMinimumHours: job.guaranteed_minimum_hours ?? undefined,
    status: job.status ?? undefined,
    jobTimezone,
    options: job.options || {},
  });
}

const timecardTime = (job: TimecardJobForTime): string | null => {
  const isTimeOff = (job.shift_type as string) === 'time_off';

  if (isTimeOff) {
    const timeOffTimezone =
      job.assignment?.primary_location?.timezone_lookup || job.location?.timezone_lookup;

    if (timeOffTimezone && job.current_starts_time && job.current_ends_time) {
      const start = momentTz(job.current_starts_time, timeOffTimezone);
      const end = momentTz(job.current_ends_time, timeOffTimezone);

      const startsAtMidnight = start.format('HH:mm') === '00:00';
      const endsAtEndOfDay = end.format('HH:mm') === '23:59';

      if (startsAtMidnight && endsAtEndOfDay) {
        return 'All day';
      }
    }
  }

  return jobTime({
    ...job,
    options: { ...(job.options || {}), withShiftLength: false },
  } as JobTimeInput & { options?: JobTimeOptions });
};

const jobTimeFormatted = (
  currentEndsTime?: string | null,
  currentStartsTime?: string | null,
  approvedCClockIn?: string | null,
  approvedClockOut?: string | null,
  jobTimezone?: string | null,
  guaranteedMinimumHours?: number | null,
  status?: string | null,
): string | null => {
  if (!currentStartsTime || !currentEndsTime) {
    return null;
  }

  const timezone = jobTimezone || momentTz.guess();
  const startsTime = approvedCClockIn || currentStartsTime;
  const endsTime = approvedClockOut || currentEndsTime;
  const start = new MedelyDateTime(startsTime, { tz: timezone });
  const end = new MedelyDateTime(endsTime, { tz: timezone });

  const hoursDiff = start.difference(end, 'hours').toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const hours =
    status === 'canceled_with_fee'
      ? guaranteedMinimumHours
      : Math.max(guaranteedMinimumHours ?? 0, +hoursDiff);

  const startFormatted = start.format('HH:mm');
  const endFormatted = end.format('HH:mm');

  return `${pluralize(hours ?? 0, 'hr')} shift • ${startFormatted} - ${endFormatted}`;
};

const jobTimeFromRange = ({
  starts_time,
  ends_time,
  timezone,
}: {
  starts_time?: string | null;
  ends_time?: string | null;
  timezone?: string | null;
}): string | null => {
  if (!starts_time || !ends_time) {
    return null;
  }

  const tzValue = timezone || momentTz.guess();
  const start = new MedelyDateTime(starts_time, { tz: tzValue });
  const end = new MedelyDateTime(ends_time, { tz: tzValue });
  const hours = start.difference(end, 'hours');

  return `${start.militaryTime}-${end.militaryTime} (${pluralize(hours, 'hr')})`;
};

const jobSpecialties = (specialties?: { id?: number | null; label?: string | null }[]) => {
  return specialties?.map((s) => s.label).join(', ') || '–';
};

const jobProcedures = (procedures?: { id?: number | null; display_name?: string | null }[]) => {
  return procedures?.map((s) => s.display_name).join(', ') || '–';
};

const jobPatientPopulation = (patientPopulation?: string | null) => {
  return titleize(patientPopulation || '–');
};

type JobListItemVariant = 'find_jobs' | 'schedule' | 'upcoming_shifts';

const jobListItemTitle = (
  job: {
    location: { name?: string };
    positions: { display_name?: string }[];
    job_type: JobTypeList;
  },
  variant?: JobListItemVariant,
) => {
  if (variant === 'upcoming_shifts') {
    return job.location.name;
  }
  if (job.job_type === 'time_off') {
    return 'Scheduled time off';
  }
  return job?.positions?.map((s) => s.display_name).join(', ') || '–';
};

const jobListItemAddress = (
  job: Pick<Job, 'job_type'> & {
    location: {
      address_city?: string;
      address_zip?: string;
      state?: { abbreviation?: string };
    };
    status: JobStatus;
  },
  variant?: JobListItemVariant,
  isAgency?: boolean,
  isSaas?: boolean,
) => {
  const isBooked = job.status === 'booked';

  if (variant === 'upcoming_shifts') {
    return `${cityStateZip(
      job.location?.address_city,
      job.location?.state?.abbreviation,
      job.location?.address_zip,
    )}`;
  }

  if (job.job_type === 'time_off') {
    return null;
  }

  if (isBooked || isAgency || isSaas) {
    return `${facilityCity(job.location)}`;
  }

  return `${cityZip(job.location)}`;
};

const jobListItemTime = (
  job: Pick<
    IJob,
    | 'approved_clock_in'
    | 'approved_clock_out'
    | 'current_ends_time'
    | 'current_starts_time'
    | 'guaranteed_minimum_hours'
    | 'status'
  > & {
    location: { timezone_lookup?: string };
    job_type: JobTypeList;
    assignment?: { primary_location?: { timezone_lookup?: string } };
  },
  currentUser?: { primary_locations?: { timezone_lookup: string }[] },
) => {
  let timezone: string;
  let options: { precision?: 0 | 1 | 2; withShiftLength?: boolean; scheduledShiftStyle?: boolean } =
    {};

  if (job.job_type === 'time_off') {
    timezone =
      job.assignment?.primary_location?.timezone_lookup ||
      currentUser?.primary_locations?.[0]?.timezone_lookup ||
      momentTz.guess();

    const start = momentTz(job.current_starts_time, timezone);
    const end = momentTz(job.current_ends_time, timezone);

    const startsAtMidnight = start.isSame(momentTz(start, timezone).startOf('day'), 'minute');
    // Time off shifts have a maximum length of 24 hours
    const endsAtMidnight = end.isSame(momentTz(end, timezone).startOf('day'), 'minute');

    if (startsAtMidnight && endsAtMidnight) {
      return 'All day';
    }

    // If not all day, show time range with timezone synthesized from user or assignment primary location
    options = { precision: 1, withShiftLength: false, scheduledShiftStyle: true };
  } else {
    timezone = job.location?.timezone_lookup || momentTz.guess();
    options = { precision: 1, scheduledShiftStyle: true };
  }

  // For non-time-off jobs, show time range with shift location timezone
  return jobTime({
    current_starts_time: job.current_starts_time,
    current_ends_time: job.current_ends_time,
    approved_clock_in: job.approved_clock_in,
    approved_clock_out: job.approved_clock_out,
    guaranteed_minimum_hours: job.guaranteed_minimum_hours,
    status: job.status,
    shift_type: job.job_type,
    assignment: job.assignment,
    location: job.location,
    options,
  });
};

type JobForDate = Pick<IJob, 'starts_date'>;
const jobDate = (job: JobForDate, format: 'fullDay' | 'short'): string | null => {
  const date = new MedelyDateTime(job.starts_date, { tz: 'system' });

  if (!date) {
    return null;
  }

  if (format === 'fullDay') {
    return date.humanizedDate;
  }

  return date.dateShortNumeric;
};

const jobWeekdayShortDate = ({
  startDate,
  timezone,
}: {
  startDate?: string | null;
  timezone?: string | null;
}): string | null => {
  if (!startDate) {
    return null;
  }

  const timezoneValue = timezone || momentTz.guess();

  return dateTimeLegacy.custom(startDate, 'dddd, MM/DD/YY', timezoneValue);
};

type JobForShiftType = Pick<IJob, 'shift_type'> & JobForIsOvernight;
const shiftType = (job: JobForShiftType, withOvernight = true) => {
  const { shift_type } = job;

  const isOverNight = withOvernight && isOvernight(job);

  if (shift_type === 'regular') {
    return isOverNight ? 'Regular shift overnight' : 'Regular shift';
  }

  if (shift_type === 'on_call') {
    return isOverNight ? 'On call overnight' : 'On call';
  }

  if (shift_type === 'call_back') {
    return isOverNight ? 'Call back overnight' : 'Call back';
  }

  if (shift_type === 'missed') {
    return 'Missed shift';
  }

  if (shift_type === 'cancelled') {
    return 'Canceled shift';
  }

  if (shift_type === 'time_off') {
    return 'Time off';
  }

  return '';
};

const positions = (positions?: { display_name?: string | null }[] | null) => {
  return positions?.map((s) => s.display_name).join(', ') || '–';
};

type JobForPaymentStatusLabel = Pick<IJob, 'status'> & {
  disputes?: Pick<IDispute, 'status'>[];
  job_billings: { payout?: Pick<IPayout, 'status'> }[];
  expense_adjustments: { payout?: Pick<IPayout, 'status'> }[];
};
const jobPaymentStatusLabel = ({
  status,
  disputes,
  job_billings,
  expense_adjustments,
}: JobForPaymentStatusLabel): { label: string; color: InfoBadgeColor } => {
  if (status === 'disputed') {
    const isWaitingOnPro = disputes?.some(
      (dispute) => dispute.status === DisputeStatus.WaitingForProToRespond,
    );
    return {
      label: isWaitingOnPro ? 'Disputed' : 'Dispute in review',
      color: 'error',
    };
  }

  const payables = [...job_billings, ...expense_adjustments];
  const isPartiallyPaid = payables.some((payable) => payable.payout?.status === 'paid');
  if (isPartiallyPaid) {
    const isFullyPaid = payables.every((payable) => payable.payout?.status === 'paid');
    return {
      label: isFullyPaid ? 'Paid' : 'Partially paid',
      color: 'success',
    };
  }

  if (status === 'held_for_dispute_review') {
    return {
      label: 'In review',
      color: 'warning',
    };
  }

  return {
    label: 'Pending',
    color: 'info',
  };
};

const cityStateZip = (
  city?: string | null,
  stateAbbreviation?: string | null,
  zip?: string | null,
) => {
  if (city) {
    return `${city}, ${stateAbbreviation ?? ''} ${zip ?? ''}`;
  } else {
    return `${stateAbbreviation ?? ''} ${zip ?? ''}`.trim();
  }
};

const facilityCity = (location: Pick<Location, 'name' | 'address_city'>) => {
  const name = location.name;

  return !name
    ? `${location?.address_city ?? ''}`
    : `${location.name ?? ''}, ${location?.address_city ?? ''}`;
};

const cityZip = (location: Pick<Location, 'address_city' | 'address_zip'>) => {
  const city = location.address_city;
  const zip = location.address_zip ?? '';

  return !city ? zip : `${city} ${zip}`.trim();
};

export const shiftStartDateTime = (
  currentEndsTime?: string | null,
  currentStartsTime?: string | null,
  approvedCClockIn?: string | null,
  approvedClockOut?: string | null,
  jobTimezone?: string | null,
  guaranteedMinimumHours?: number | null,
  status?: string | null,
  startsDate?: string | null,
): string => {
  const tz = jobTimezone || 'system';

  const date = startsDate
    ? new MedelyDateTime(startsDate, { keepLocalTime: true, tz })
    : new MedelyDateTime(currentStartsTime ?? '', { tz });

  const time = jobTime({
    current_ends_time: currentEndsTime || undefined,
    current_starts_time: currentStartsTime || undefined,
    approved_clock_in: approvedCClockIn || undefined,
    approved_clock_out: approvedClockOut || undefined,
    guaranteed_minimum_hours: guaranteedMinimumHours ?? undefined,
    status: (status as JobStatus) || undefined,
    location: jobTimezone ? { timezone_lookup: jobTimezone } : undefined,
    options: { scheduledShiftStyle: true },
  });
  return `${date.humanizedDate} • ${time}`;
};

// assigments
const assignmentPositions = (positions?: { display_name?: string | null }[] | null) => {
  return positions?.map((p) => p.display_name).join(', ') || '–';
};

const formatSpecialties = (item: { label?: string | null }[], maxLabelSize?: number): string => {
  const specialtiesCount = item.length ?? 0;
  const hasMultipleSpecialties = specialtiesCount > 1;
  const label = item[0].label ?? '';
  const isBigLabel = (label.length ?? 0) > (maxLabelSize ?? Infinity);

  const specialty = isBigLabel ? `${label.slice(0, maxLabelSize)}...` : label;
  const otherSpecialties = hasMultipleSpecialties ? ` +${specialtiesCount - 1} more` : '';

  return specialty.concat(otherSpecialties);
};

const shiftStartDate = (timezone?: string | null, startsDate?: string | null) => {
  const tz = timezone || 'system';

  if (startsDate) {
    const date = new MedelyDateTime(startsDate, { keepLocalTime: true, tz });

    return `${date.dateShortNumeric}`;
  }
};

const availableAssignmentTime = (
  timezone?: string | null,
  startsTime?: string | null,
  endsTime?: string | null,
): string | null => {
  const shiftTimezone = timezone || momentTz.guess();
  const start = new MedelyDateTime(startsTime as string, {
    tz: shiftTimezone,
  });

  const end = new MedelyDateTime(endsTime as string, {
    tz: shiftTimezone,
  });

  return `${start.militaryTime} - ${end.militaryTime}`;
};

const availablePermanentAssignmentTimes = (
  numberOfShifts?: number | null,
  scheduleType?: string | null,
  timezone?: string | null,
  startsTime?: string | null,
  endsTime?: string | null,
) => {
  const scheduleTypes = {
    flexible: '· Flexible',
    weekdays: '· Mon - Fri',
    weekends: '· Weekends',
  };
  const hoursPerShiftInfo = permanentTotalHoursPerWeek(
    timezone,
    startsTime,
    endsTime,
    numberOfShifts,
  );
  const scheduleTypeInfo = scheduleTypes[scheduleType as keyof typeof scheduleTypes] || '';
  const assignmentTime = availableAssignmentTime(timezone, startsTime, endsTime);
  return `${hoursPerShiftInfo} ${scheduleTypeInfo} · ${assignmentTime}`;
};

const availablePayRange = (
  minHourlyRateCents?: number | null,
  maxHourlyRateCents?: number | null,
) => {
  const minRateCents = centsToCurrency(minHourlyRateCents ?? 0);
  const maxRateCents = centsToCurrency(maxHourlyRateCents ?? 0);
  return `${minRateCents} - ${maxRateCents}`;
};

const permanentBonus = (bonusCents?: number | null) => {
  const bonus = centsToCurrency(bonusCents ?? 0);
  return `${bonus}`;
};

const assignmentTime = (
  shiftTimezone?: string | null,
  startsTime?: string | null,
  endsTime?: string | null,
  numberOfShifts?: number | null,
) => {
  const format = 'HH:mm';

  const timezone = shiftTimezone || momentTz.guess();
  const start = new MedelyDateTime(startsTime as string, {
    tz: timezone,
  });

  const end = new MedelyDateTime(endsTime as string, {
    tz: timezone,
  });
  // hoursDiff is specifically the raw ends - starts time.
  // Do not confuse this with assignment.hours_per_shift, which deducts estimated break time.
  const hoursDiff = end.difference(start, 'hours');

  return `${numberOfShifts}x${hoursDiff.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}hr shifts • ${start.format(format)} - ${end.format(format)}`;
};

const permanentTotalHoursPerWeek = (
  shiftTimezone?: string | null,
  startsTime?: string | null,
  endsTime?: string | null,
  numberOfShifts?: number | null,
) => {
  const timezone = shiftTimezone || momentTz.guess();
  const start = new MedelyDateTime(startsTime as string, {
    tz: timezone,
  });

  const end = new MedelyDateTime(endsTime as string, {
    tz: timezone,
  });
  const hoursDiff = start.difference(end, 'hours');

  return `${numberOfShifts ? numberOfShifts * hoursDiff : hoursDiff} hrs/wk`;
};

const assignmentExpectedHours = (
  hoursPerShift?: number | null,
  numberOfShiftsPerWeek?: number | null,
) => {
  const hasData =
    hoursPerShift !== undefined &&
    numberOfShiftsPerWeek !== undefined &&
    hoursPerShift !== null &&
    numberOfShiftsPerWeek !== null;
  const result = hasData
    ? (hoursPerShift * numberOfShiftsPerWeek).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : 0;
  return result;
};

const assignmentStartDateAndEndDate = (
  shiftTimezone?: string | null,
  startsDate?: string | null,
  endsDate?: string | null,
) => {
  const timezone = shiftTimezone || momentTz.guess();
  const start = new MedelyDateTime(startsDate as string, {
    tz: timezone,
  });

  const end = new MedelyDateTime(endsDate as string, {
    tz: timezone,
  });

  const numberOfWeeks = start.difference(end, 'weeks');

  return `${start.dateShortNumeric} - ${end.dateShortNumeric} · ${pluralize(
    numberOfWeeks,
    'week',
  )}`;
};

const assignmentProcedures = (
  surgeryTypes?: { id?: number | null; display_name?: string | null }[],
) => {
  return surgeryTypes?.map((s) => s.display_name).join(', ') || '–';
};

const assignmentPatientPopulation = (patientPopulation: string) => {
  return titleize(patientPopulation || '–');
};

function formatDateTime(dateTime: MomentInput, format: string, timezone?: string): string {
  if (!dateTime) {
    return '';
  }
  if (timezone) {
    return tz(dateTime, timezone).format(format);
  }
  return moment.parseZone(dateTime).format(format);
}

export const dateTimeLegacy = {
  date: (date: string, tz?: string, format = 'MM/DD/YY') => formatDateTime(date, format, tz),
  time: (date: string, tz?: string) => formatDateTime(date, 'HH:mm', tz),
  custom: (date: string, format: string, tz?: string) => formatDateTime(date, format, tz),
};

const DisplayHelpers = {
  expenseAdjustment: { taxableTypeAsTitle },
  humanizeDate,
  getYear,
  getDate,
  dateTime: dateTimeLegacy,
  job: {
    date: jobDate,
    weekdayShortDate: jobWeekdayShortDate,
    jobTotalHoursNumber,
    patientPopulation: jobPatientPopulation,
    paymentStatusLabel: jobPaymentStatusLabel,
    positions,
    procedures: jobProcedures,
    shiftStartDateTime,
    shiftType,
    specialties: jobSpecialties,
    time: jobTime,
    timeFromRange: jobTimeFromRange,
    timeFormatted: jobTimeFormatted,
    timecardTime,
    listItem: {
      address: jobListItemAddress,
      time: jobListItemTime,
      positionsTitle: jobListItemTitle,
    },
  },
  assignment: {
    positions: assignmentPositions,
    specialties: formatSpecialties,
    startDate: shiftStartDate,
    shiftTime: availableAssignmentTime,
    time: assignmentTime,
    expectedHours: assignmentExpectedHours,
    assignmentStartDateAndEndDate,
    procedures: assignmentProcedures,
    patientPopulation: assignmentPatientPopulation,
  },
  permanentPlacement: {
    positions: assignmentPositions,
    specialties: formatSpecialties,
    startDate: shiftStartDate,
    availablePermanentAssignmentTimes,
    availablePayRange,
    permanentBonus,
    time: assignmentTime,
    permanentTotalHoursPerWeek,
  },
  location: {
    cityStateZip,
  },
  titleize,
};

export default DisplayHelpers;
